import { meResponseSchema, passwordSchema } from '@koderek/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Anchor, Button, PasswordInput, Stack } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';
import { ApiError, api } from '../api/client';
import { AuthCard } from '../features/auth/AuthCard';
import { errorMessage, useSetMe } from '../features/auth/session';
import { t } from '../texts/pl';

// Powtórzenie hasła tylko w formularzu – do API idzie samo hasło.
const formSchema = z
  .object({ password: passwordSchema, passwordRepeat: z.string() })
  .refine((v) => v.password === v.passwordRepeat, { path: ['passwordRepeat'], message: t.fields.passwordMismatch });
type FormValues = z.infer<typeof formSchema>;

/** US-206 AC-3…AC-5: nowe hasło z linku resetu albo pierwsze hasło z zaproszenia; po zapisie użytkownik jest zalogowany. */
export function SetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const invitation = params.get('zaproszenie') === '1';
  const navigate = useNavigate();
  const setMe = useSetMe();
  const form = useForm<FormValues>({ resolver: zodResolver(formSchema) });
  const save = useMutation({
    mutationFn: (values: FormValues) =>
      api('POST', '/auth/password', { body: { token, password: values.password }, schema: meResponseSchema }),
    onSuccess: (me) => {
      setMe(me);
      void navigate('/konto', { replace: true });
    },
  });
  const expired = save.error instanceof ApiError && (save.error.code === 'token_expired' || save.error.code === 'token_invalid');

  return (
    <AuthCard title={invitation ? t.setPassword.titleInvitation : t.setPassword.titleReset}>
      {expired ? (
        <Stack gap="sm">
          <Alert color="orange" role="alert">
            {t.setPassword.expired}
          </Alert>
          <Anchor component={Link} to="/nie-pamietam-hasla">
            {t.login.forgot}
          </Anchor>
        </Stack>
      ) : (
        <form onSubmit={form.handleSubmit((values) => save.mutate(values))} noValidate>
          <Stack gap="sm">
            <PasswordInput
              label={t.fields.newPassword}
              description={t.fields.passwordHint}
              autoComplete="new-password"
              required
              {...form.register('password')}
              error={form.formState.errors.password?.message}
            />
            <PasswordInput
              label={t.fields.passwordRepeat}
              autoComplete="new-password"
              required
              {...form.register('passwordRepeat')}
              error={form.formState.errors.passwordRepeat?.message}
            />
            {save.isError ? (
              <Alert color="red" role="alert">
                {errorMessage(save.error)}
              </Alert>
            ) : null}
            <Button type="submit" loading={save.isPending} disabled={!token}>
              {t.setPassword.submit}
            </Button>
          </Stack>
        </form>
      )}
    </AuthCard>
  );
}
